import type { MissionItem, DeviceItem } from './missionModels';

// ── Operation lifecycle ──────────────────────────────────────────────────────

export type OperationStatus = 'draft' | 'planned' | 'dispatching' | 'running' | 'completed' | 'failed' | 'cancelled';

export type OperationMissionStatus = 'pending' | 'ready' | 'dispatched' | 'running' | 'completed' | 'failed' | 'skipped';

export type OperationMissionTrigger = 'auto' | 'manual' | 'on_event';

export type DeviceCommandStatus = 'queued' | 'acknowledged' | 'executing' | 'completed' | 'failed' | 'expired' | 'cancelled';

export type TelemetryType =
    | 'heartbeat'
    | 'position'
    | 'unit_status'
    | 'module_status'
    | 'mission_progress'
    | 'sensor_reading'
    | 'error'
    | 'status_change'
    | 'power_cycle';

// ── Operation & OperationMission ─────────────────────────────────────────────

export type OperationMissionItem = {
    id: number;
    operationId: number;
    missionId: number;
    deviceId?: number | null;
    executionOrder: number;
    triggerType?: OperationMissionTrigger;
    triggerCondition?: OperationMissionTrigger;
    triggerConfig?: Record<string, any> | null;
    triggerEventConfig?: Record<string, any> | null;
    dependsOnOperationMissionIds: number[];
    status: OperationMissionStatus;
    startedAt?: string | null;
    completedAt?: string | null;
    failureReason?: string | null;
    measurementCount?: number;
    lastMeasurementAt?: string | null;
    mission?: MissionItem | null;
    device?: DeviceItem | null;
};

export type OperationItem = {
    id: number;
    name: string;
    description?: string | null;
    status: OperationStatus;
    companyId: number;
    areaId?: number | null;
    createdByUserId?: number | null;
    scheduledAt?: string | null;
    scheduledStartAt?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    failedAt?: string | null;
    cancelledAt?: string | null;
    measurementCount?: number;
    lastMeasurementAt?: string | null;
    metadata?: Record<string, any> | null;
    config?: Record<string, any> | null;
    createdAt?: string;
    updatedAt?: string;
    operationMissions?: OperationMissionItem[];
    area?: { id: number; name: string } | null;
};

// ── DeviceCommand ────────────────────────────────────────────────────────────

export type DeviceCommandType =
    | 'stage_mission'
    | 'start_mission'
    | 'abort_mission'
    | 'pause_mission'
    | 'resume_mission'
    | 'goto_point'
    | 'return_home'
    | 'capture'
    | 'set_speed'
    | 'emergency_stop'
    | 'calibrate'
    | 'custom';

export type DeviceCommandItem = {
    id: number;
    companyId: number;
    deviceId: number;
    missionId?: number | null;
    operationId?: number | null;
    commandType: DeviceCommandType | string;
    payload?: Record<string, any> | null;
    priority: number;
    status: DeviceCommandStatus;
    result?: Record<string, any> | null;
    expiresAt?: string | null;
    acknowledgedAt?: string | null;
    startedAt?: string | null;
    completedAt?: string | null;
    createdAt?: string;
    device?: DeviceItem | null;
    mission?: MissionItem | null;
};

// ── DeviceTelemetry ──────────────────────────────────────────────────────────

export type DeviceTelemetryItem = {
    id: number;
    companyId: number;
    deviceId: number;
    missionId?: number | null;
    telemetryType: TelemetryType | string;
    clientEventId?: string | null;
    payload?: Record<string, any> | null;
    deviceTimestamp?: string | null;
    createdAt?: string;
};

export type DeviceVideoStreamItem = {
    id: number;
    deviceId: number;
    companyId: number;
    cameraKey: string;
    label: string;
    enabled: boolean;
    status: 'live' | 'waiting' | 'stale' | 'disabled' | string;
    streamUrl?: string | null;
    snapshotUrl?: string | null;
    lastFrameAt?: string | null;
    latestFrameSequence?: number | null;
    latestFrameContentType?: string | null;
    meta?: Record<string, any> | null;
};

// ── Status display helpers ───────────────────────────────────────────────────

export const OPERATION_STATUS_OPTIONS: Array<{ value: OperationStatus; label: string; color: string }> = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'planned', label: 'Planned', color: 'blue' },
    { value: 'dispatching', label: 'Dispatching', color: 'cyan' },
    { value: 'running', label: 'Running', color: 'green' },
    { value: 'completed', label: 'Completed', color: 'purple' },
    { value: 'failed', label: 'Failed', color: 'red' },
    { value: 'cancelled', label: 'Cancelled', color: 'volcano' },
];

export const OP_MISSION_STATUS_OPTIONS: Array<{ value: OperationMissionStatus; label: string; color: string }> = [
    { value: 'pending', label: 'Pending', color: 'default' },
    { value: 'ready', label: 'Ready', color: 'blue' },
    { value: 'dispatched', label: 'Dispatched', color: 'cyan' },
    { value: 'running', label: 'Running', color: 'green' },
    { value: 'completed', label: 'Completed', color: 'purple' },
    { value: 'failed', label: 'Failed', color: 'red' },
    { value: 'skipped', label: 'Skipped', color: 'default' },
];

export const COMMAND_STATUS_OPTIONS: Array<{ value: DeviceCommandStatus; label: string; color: string }> = [
    { value: 'queued', label: 'Queued', color: 'orange' },
    { value: 'acknowledged', label: 'Acknowledged', color: 'blue' },
    { value: 'executing', label: 'Executing', color: 'green' },
    { value: 'completed', label: 'Completed', color: 'purple' },
    { value: 'failed', label: 'Failed', color: 'red' },
    { value: 'expired', label: 'Expired', color: 'default' },
    { value: 'cancelled', label: 'Cancelled', color: 'volcano' },
];

export const COMMAND_TYPE_OPTIONS: Array<{ value: DeviceCommandType; label: string }> = [
    { value: 'stage_mission', label: 'Send mission' },
    { value: 'start_mission', label: 'Start mission' },
    { value: 'abort_mission', label: 'Abort mission' },
    { value: 'pause_mission', label: 'Pause mission' },
    { value: 'resume_mission', label: 'Resume mission' },
    { value: 'goto_point', label: 'Go to point' },
    { value: 'return_home', label: 'Return home' },
    { value: 'capture', label: 'Capture' },
    { value: 'set_speed', label: 'Set speed' },
    { value: 'emergency_stop', label: 'Emergency stop' },
    { value: 'calibrate', label: 'Calibrate' },
    { value: 'custom', label: 'Custom' },
];

export const TRIGGER_TYPE_OPTIONS: Array<{ value: OperationMissionTrigger; label: string; description: string }> = [
    { value: 'auto', label: 'Automatic', description: 'Dispatched automatically when dependencies complete' },
    { value: 'manual', label: 'Manual', description: 'Operator must manually trigger this step' },
    { value: 'on_event', label: 'On event', description: 'Triggered when a specific event/condition is met' },
];

export const operationStatusColor = (status: string): string => {
    const found = OPERATION_STATUS_OPTIONS.find((o) => o.value === status);
    return found?.color || 'default';
};

export const opMissionStatusColor = (status: string): string => {
    const found = OP_MISSION_STATUS_OPTIONS.find((o) => o.value === status);
    return found?.color || 'default';
};

export const commandStatusColor = (status: string): string => {
    const found = COMMAND_STATUS_OPTIONS.find((o) => o.value === status);
    return found?.color || 'default';
};
